import { HStack, PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { DataFetchInfo } from '@/components/DataFetchInfo'
import { Filter } from '@/components/DataTable/FilterControls'
import { DateRange } from '@/components/DataTable/FilterControls/Filters/DateRangeFilter'
import { useCustomerErpOptions } from '@/hooks/queries/credit/useCustomers'
import {
  BLOCKED_ORDER_COCKPIT_GROUP_BYS,
  blockedOrdersSearchSchema,
  DEFAULT_BLOCKED_ORDER_COCKPIT_GROUP_BY,
  useBlockedOrderGroups,
  useBlockedOrders,
  useBlockedOrderSource,
  type BlockedOrderCockpitGroupBy,
} from '@/hooks/queries/credit/useBlockedOrders'
import { useProvinceOrderCutoffList } from '@/hooks/queries/credit/useProvinceOrderCutoffs'
import { useTablePagination } from '@/hooks/useTablePagination'
import { formatDoaBandLabel } from '@/lib/utils/doaBands'
import {
  ComboboxSearchControl,
  DateRangeControl,
  MultipleToggleListControl,
  TextInputControl,
} from '@/lib/utils/filters'
import {
  BLOCKED_ORDER_OPTION_KEYS,
  BLOCKED_ORDER_COCKPIT_STATUSES,
  BLOCKED_ORDER_SAP_HANDOFF_STATUSES,
  CUSTOMER_GROUP_1_PROVINCES,
  getProvinceName,
  isEscalatedBlockedOrderOptionKey,
  isProvinceCode,
  type BlockedOrderCockpitStatus,
  type BlockedOrderSapHandoffStatus,
  type ProvinceCode,
} from '@repo/constants'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { toCamelCase } from '@repo/utils'
import { useTranslation } from 'react-i18next'
import { BlockedOrderGroupsTable } from './-components/BlockedOrderGroupsTable'
import { BlockedOrderSapHandoffBanner } from './-components/BlockedOrderSapHandoffBanner'
import { BlockedOrderSourceToggle } from './-components/BlockedOrderSourceToggle'
import { BlockedOrdersDataTable } from './-components/BlockedOrdersDataTable'
import { CreateTestBlockedOrdersDialog } from './-components/CreateTestBlockedOrdersDialog'

export const Route = createFileRoute('/_auth/_modules/credit/blocked-orders/')({
  component: RouteComponent,
  validateSearch: (search) => blockedOrdersSearchSchema.parse(search),
})

/**
 * The blocked-orders cockpit (CT-30).
 *
 * Shows the whole picture to anyone in Credit — including the orders sitting above their own
 * authority — which is why it is not filtered by the viewer's assignments.
 *
 * It opens grouped by decision: a payer's orders awaiting review, those awaiting approval, and each
 * decision that released or rejected some, each row opening its group's page
 * (`groups/$groupKey`), where a pending group is decided. Grouped by nothing it is one row per
 * order, each opening that order's record.
 */
function RouteComponent() {
  const { t } = useTranslation(['credit', 'common'])
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const [keyword, setKeyword] = useState<string>(search.keyword ?? '')

  // Each ERP dropdown searches the server on its own as the user types in it.
  const [payerErps, setPayerErps] = useState<string[]>(search.payerErp ?? [])
  const payerErpOptions = useCustomerErpOptions(payerErps)
  const payerErpFilter = {
    control: ComboboxSearchControl,
    name: 'payerErp',
    title: t('blockedOrders.cockpit.filters.payerErp'),
    options: payerErpOptions.options,
    value: payerErps,
    onChange: setPayerErps,
    loading: payerErpOptions.isPending,
    onSearchChange: payerErpOptions.onSearchChange,
    isSearching: payerErpOptions.isSearching,
    onEndReached: payerErpOptions.onEndReached,
    hasMoreOptions: payerErpOptions.hasMoreOptions,
    isLoadingMore: payerErpOptions.isLoadingMore,
  }

  const [soldToErps, setSoldToErps] = useState<string[]>(search.soldToErp ?? [])
  const soldToErpOptions = useCustomerErpOptions(soldToErps)
  const soldToErpFilter = {
    control: ComboboxSearchControl,
    name: 'soldToErp',
    title: t('blockedOrders.cockpit.columns.soldToErp'),
    options: soldToErpOptions.options,
    value: soldToErps,
    onChange: setSoldToErps,
    loading: soldToErpOptions.isPending,
    onSearchChange: soldToErpOptions.onSearchChange,
    isSearching: soldToErpOptions.isSearching,
    onEndReached: soldToErpOptions.onEndReached,
    hasMoreOptions: soldToErpOptions.hasMoreOptions,
    isLoadingMore: soldToErpOptions.isLoadingMore,
  }

  const [poNumber, setPoNumber] = useState<string>(search.poNumber ?? '')
  const poNumberFilter = {
    control: TextInputControl,
    name: 'poNumber',
    title: t('blockedOrders.cockpit.columns.poNumber'),
    options: [],
    value: poNumber,
    onChange: setPoNumber,
  }

  // Only the provinces BAT's Customer Group 1 list maps to: an order with an unmapped code shows
  // that code raw, and has no province to pick here.
  const [provinces, setProvinces] = useState<string[]>(search.province ?? [])
  const provinceFilter = {
    control: MultipleToggleListControl,
    name: 'province',
    title: t('blockedOrders.cockpit.columns.province'),
    options: CUSTOMER_GROUP_1_PROVINCES.map((code) => ({
      value: code,
      label: `${t(`province.${toCamelCase(getProvinceName(code))}`, { ns: 'common' })} (${code})`,
    })),
    value: provinces,
    onChange: setProvinces,
  }

  const [statuses, setStatuses] = useState<BlockedOrderCockpitStatus[]>(search.status ?? [])
  const statusFilter = {
    control: MultipleToggleListControl,
    name: 'status',
    title: t('blockedOrders.cockpit.filters.status'),
    options: BLOCKED_ORDER_COCKPIT_STATUSES.map((status) => ({
      value: status,
      label: t(`blockedOrders.cockpit.status.${status}`),
    })),
    value: statuses,
    onChange: setStatuses,
  }

  // Beside the status, never instead of it: the decision stays the decision, this is where its
  // hand-off to SAP stands. Only decided orders on the live source can match.
  const [sapHandoffs, setSapHandoffs] = useState<BlockedOrderSapHandoffStatus[]>(
    search.sapHandoff ?? [],
  )
  const sapHandoffFilter = {
    control: MultipleToggleListControl,
    name: 'sapHandoff',
    title: t('blockedOrders.cockpit.filters.sapHandoff'),
    options: BLOCKED_ORDER_SAP_HANDOFF_STATUSES.map((status) => ({
      value: status,
      label: t(`blockedOrders.sap.status.${status}`),
    })),
    value: sapHandoffs,
    onChange: setSapHandoffs,
  }

  const [doaOptionKeys, setDoaOptionKeys] = useState<string[]>(search.doaOptionKey ?? [])
  // A searchable dropdown: there are too many bands to lay out as toggles.
  const doaFilter = {
    control: ComboboxSearchControl,
    name: 'doaOptionKey',
    title: t('blockedOrders.cockpit.filters.doaLevel'),
    // Declared in ascending CEB order, which is the order a reviewer thinks about them in — left
    // unsorted deliberately, unlike the CLR kind filter's alphabetical labels.
    // Each band up to $100K has an escalated twin with the same range, so the twin says so —
    // otherwise the two read as one option listed twice.
    options: BLOCKED_ORDER_OPTION_KEYS.map((key) => ({
      value: key,
      label: isEscalatedBlockedOrderOptionKey(key)
        ? `${formatDoaBandLabel(key, t)} · ${t('delegationOfAuthority.escalation.label')}`
        : formatDoaBandLabel(key, t),
    })),
    value: doaOptionKeys,
    onChange: setDoaOptionKeys,
    hasHorizontalScroll: true,
  }

  const [createdRange, setCreatedRange] = useState<DateRange>({
    from: search.createdFrom,
    to: search.createdTo,
  })
  const createdOnFilter = {
    control: DateRangeControl,
    name: 'createdOn',
    title: t('blockedOrders.cockpit.columns.caseCreatedOn'),
    options: [],
    value: createdRange,
    onChange: setCreatedRange,
    fromParamName: 'createdFrom',
    toParamName: 'createdTo',
  }

  const filters = [
    payerErpFilter,
    soldToErpFilter,
    poNumberFilter,
    provinceFilter,
    statusFilter,
    sapHandoffFilter,
    doaFilter,
    createdOnFilter,
  ]

  // Live SAP cases or test orders, per viewer. Nothing is fetched until it is known whether this
  // environment can serve the viewer's source at all.
  const { data: sourceState } = useBlockedOrderSource()
  const canRead = sourceState?.isSupported === true

  const groupBy: BlockedOrderCockpitGroupBy =
    search.groupBy ?? DEFAULT_BLOCKED_ORDER_COCKPIT_GROUP_BY
  const blockedOrdersQuery = useBlockedOrders(
    { ...search, pageSize },
    { enabled: canRead && groupBy === 'none' },
  )
  const groupsQuery = useBlockedOrderGroups(
    { ...search, pageSize },
    { enabled: canRead && groupBy === 'decision' },
  )
  const activeQuery = groupBy === 'decision' ? groupsQuery : blockedOrdersQuery

  // What a row is changes with this, so it goes back to the first page.
  const groupBySelect = (
    <Select
      value={groupBy}
      onValueChange={(value) =>
        navigate({
          search: { ...search, groupBy: value as BlockedOrderCockpitGroupBy, page: 1 },
        })
      }
    >
      <SelectTrigger
        className="bg-background h-8 w-48"
        aria-label={t('blockedOrders.batch.groupBy')}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {BLOCKED_ORDER_COCKPIT_GROUP_BYS.map((value) => (
          <SelectItem key={value} value={value}>
            {t(`blockedOrders.batch.groupByOptions.${value}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  // The cutoff times, for the countdown each row renders. One small query for the whole page
  // rather than a value per row: it is thirteen rows of reference data that changes by hand.
  const { data: cutoffs, refetch: refetchCutoffs } = useProvinceOrderCutoffList()
  const cutoffTimeByProvince = new Map<ProvinceCode, string>(
    (cutoffs?.results ?? []).flatMap((row) =>
      isProvinceCode(row.provinceCode) && row.cutoffTime
        ? [[row.provinceCode, row.cutoffTime] as const]
        : [],
    ),
  )

  /**
   * "Last Refresh time" — the first thing the Excel cockpit puts on screen, and the one piece of it
   * that had no equivalent here.
   *
   * It earns its place on this page specifically: every row is a countdown to a shipping cutoff, so
   * how old the numbers are changes what they mean, and the normal way this page is found is a tab
   * left open since yesterday. The timestamp is when the list was last fetched, not when SAP last
   * sent anything — the cockpit has no claim to make about the upstream feed.
   */
  const [isRefreshAnimating, setIsRefreshAnimating] = useState(false)
  const [isManualRefresh, setIsManualRefresh] = useState(false)

  const refresh = () => {
    setIsRefreshAnimating(true)
    setIsManualRefresh(true)
    void Promise.all([activeQuery.refetch(), refetchCutoffs()]).finally(() => {
      // Hold the refreshing state briefly even on a fast round-trip, so a press always visibly
      // does something.
      setTimeout(() => {
        setIsRefreshAnimating(false)
        setIsManualRefresh(false)
      }, 1000)
    })
  }

  const showResults = () => {
    navigate({
      search: {
        ...search,
        keyword: keyword || undefined,
        payerErp: payerErps.length ? payerErps : undefined,
        soldToErp: soldToErps.length ? soldToErps : undefined,
        poNumber: poNumber.trim() || undefined,
        province: provinces.length ? provinces : undefined,
        status: statuses.length ? statuses : undefined,
        sapHandoff: sapHandoffs.length ? sapHandoffs : undefined,
        doaOptionKey: doaOptionKeys.length ? doaOptionKeys : undefined,
        createdFrom: createdRange.from,
        createdTo: createdRange.to,
        page: 1,
      },
    })
  }

  const resetAllFilters = () => {
    setKeyword('')
    setPayerErps([])
    setSoldToErps([])
    setPoNumber('')
    setProvinces([])
    setStatuses([])
    setSapHandoffs([])
    setDoaOptionKeys([])
    setCreatedRange({ from: undefined, to: undefined })

    navigate({
      search: {
        ...search,
        keyword: undefined,
        payerErp: undefined,
        soldToErp: undefined,
        poNumber: undefined,
        province: undefined,
        status: undefined,
        sapHandoff: undefined,
        doaOptionKey: undefined,
        caseType: undefined,
        createdFrom: undefined,
        createdTo: undefined,
        page: 1,
      },
    })
  }

  const tableSearch = {
    value: keyword,
    setValue: setKeyword,
    placeholder: t('blockedOrders.cockpit.searchPlaceholder'),
  }

  return (
    <PageContainer maxWidth="wide">
      <Spread align="end">
        <PageHeader>{t('blockedOrders.cockpit.pageHeader')}</PageHeader>
        <HStack gap="md" className="items-center">
          {sourceState && <BlockedOrderSourceToggle source={sourceState.source} />}
          {/* TEMPORARY — test orders go to the mock source, so the button lives there. */}
          {sourceState?.source === 'mock' && <CreateTestBlockedOrdersDialog />}
          <DataFetchInfo
            dataUpdatedAt={activeQuery.dataUpdatedAt}
            hasData={activeQuery.dataUpdatedAt > 0}
            onRefresh={refresh}
            isFetching={activeQuery.isFetching}
            isAnimating={isRefreshAnimating}
            shouldShowRefreshing={isManualRefresh && (activeQuery.isFetching || isRefreshAnimating)}
          />
        </HStack>
      </Spread>

      <BlockedOrderSapHandoffBanner isEnabled={canRead && sourceState?.source === 'live'} />

      <Card>
        <CardContent className="pt-6">
          {sourceState && !sourceState.isSupported ? (
            <div className="text-muted-foreground py-16 text-center text-sm">
              <p className="text-foreground font-medium">
                {t('blockedOrders.source.unsupported.title')}
              </p>
              <p className="mt-1">{t('blockedOrders.source.unsupported.description')}</p>
            </div>
          ) : groupBy === 'decision' ? (
            <BlockedOrderGroupsTable
              filters={filters as Filter<unknown>[]}
              search={tableSearch}
              toolbarActions={groupBySelect}
              pageSize={pageSize}
              onPageSizeChange={onPageSizeChange}
              groupsQuery={groupsQuery}
              onShowResults={showResults}
              onResetAllFilters={resetAllFilters}
            />
          ) : (
            <BlockedOrdersDataTable
              filters={filters as Filter<unknown>[]}
              search={tableSearch}
              toolbarActions={groupBySelect}
              pageSize={pageSize}
              onPageSizeChange={onPageSizeChange}
              blockedOrdersQuery={blockedOrdersQuery}
              cutoffTimeByProvince={cutoffTimeByProvince}
              onShowResults={showResults}
              onResetAllFilters={resetAllFilters}
            />
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}
